import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Box, Truck, CreditCard, Send, Loader2, ShieldCheck, Scale } from 'lucide-react';

interface FormData {
  companyName: string;
  name: string;
  email: string;
  contact: string;
  products: string;
  orderQuantity: string;
  deliveryAddress: string;
  deliveryDate: string;
  paymentMethod: string;
  preferredContactMethod: string;
  notes: string;
}

interface FormErrors {
  [key: string]: string;
}

const Wholesale: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    name: '',
    email: '',
    contact: '',
    products: '',
    orderQuantity: '',
    deliveryAddress: '',
    deliveryDate: '',
    paymentMethod: '',
    preferredContactMethod: '',
    notes: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
    if (errors[name]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Contact Name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Valid Email is required";
    if (!formData.contact.trim() || formData.contact.length < 10) newErrors.contact = "Valid 10-digit Phone Number is required";
    if (!formData.products) newErrors.products = "Please select a product category";
    if (!formData.orderQuantity || Number(formData.orderQuantity) <= 0) newErrors.orderQuantity = "Please enter a valid weight in KG";
    if (!formData.deliveryAddress.trim()) newErrors.deliveryAddress = "Delivery Destination is required";
    if (!formData.deliveryDate) newErrors.deliveryDate = "Required Delivery Date is required";
    if (!formData.paymentMethod) newErrors.paymentMethod = "Please select a settlement protocol";
    if (!formData.preferredContactMethod) newErrors.preferredContactMethod = "Please select a communication channel";
    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length === 0) {
      setIsLoading(true);
      try {
        const response = await fetch("https://formsubmit.co/wardhamushroom@gmail.com", {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json' 
          },
          body: JSON.stringify({
            ...formData,
            _subject: "🚨 New Bulk Commercial B2B Order Inquiry",
            _template: "table",
            _captcha: "false"
          }),
        });

        if (response.ok) {
          setSubmitted(true);
          setFormData({
            companyName: '', name: '', email: '', contact: '', products: '', orderQuantity: '',
            deliveryAddress: '', deliveryDate: '', paymentMethod: '', preferredContactMethod: '', notes: ''
          });
          setTimeout(() => setSubmitted(false), 7000);
        } else {
          throw new Error('Form submission failed');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors({ submit: 'Failed to submit inquiry. Please try again or call directly.' });
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(formErrors);
    }
  }, [validateForm, formData]);

  return (
    <div className="bg-gray-50 min-h-screen font-sans antialiased">
      
      {/* HERO SECTION */}
      <motion.div
        className="relative h-[60vh] flex flex-col justify-center items-center text-center px-4 bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('/images/wholesale2.webp')` }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto z-10">
          <span className="bg-green-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 inline-block">
            B2B Commercial Supply & Logistics
          </span>
          <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 drop-shadow-md">
            Direct Farm-to-Business Commercial Supply
          </h1>
          <p className="text-gray-200 text-base md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed drop-shadow">
            Supplying premium White Button and Oyster mushrooms to modern retail chains, supermarkets, food processing industries, and hospitality sectors with guaranteed harvest schedules.
          </p>
          <motion.button
            className="bg-green-600 hover:bg-green-700 text-white font-bold text-sm md:text-base uppercase tracking-wider px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition duration-300"
            onClick={scrollToForm}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Request Commercial Quote
          </motion.button>
        </div>
      </motion.div>

      {/* CORE ADVANTAGES */}
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Built for Commercial Scalability & Reliability
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            We understand corporate B2B trade requirements. From maintaining consistent weight metrics to executing tight delivery timelines, we keep your commercial pipelines running seamlessly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
          {/* Left Column */}
          <div className="space-y-10">
            <motion.div className="flex gap-4" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="flex-shrink-0 p-3 bg-green-100 rounded-xl h-12 w-12 flex items-center justify-center text-green-600"><CheckCircle size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Regulated Climate Controls</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Cultivated in advanced vertical farms using automated air handling units and premium insulation to preserve absolute uniform button and oyster shapes.</p>
              </div>
            </motion.div>

            <motion.div className="flex gap-4" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="flex-shrink-0 p-3 bg-blue-100 rounded-xl h-12 w-12 flex items-center justify-center text-blue-600"><Scale size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Zero Moisture Loss Weight Policy</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Fresh harvests are accurately measured post-sorting. Our efficient regional logistics network eliminates transit delays, keeping natural weight loss to an absolute minimum.</p>
              </div>
            </motion.div>

            <motion.div className="flex gap-4" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <div className="flex-shrink-0 p-3 bg-amber-100 rounded-xl h-12 w-12 flex items-center justify-center text-amber-600"><Box size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Industry-Grade Bulk Packaging</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Choose specialized packaging solutions: well-ventilated, stackable crates for fresh button mushrooms, or moisture-protected deep bulk bags for sun-dried stocks.</p>
              </div>
            </motion.div>
          </div>

          {/* Center Focus Block */}
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-white p-4 flex flex-col justify-between">
              <img src="/images/wholesale.webp" alt="Bulk Wholesale Operations" className="w-full h-44 object-cover rounded-xl mb-2" />
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 text-center">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Production Standards</p>
                <p className="text-base font-bold text-green-700">Commercial Grade Facilities</p>
              </div>
            </div>
          </motion.div>

          {/* Right Column */}
          <div className="space-y-10">
            <motion.div className="flex gap-4" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="flex-shrink-0 p-3 bg-red-100 rounded-xl h-12 w-12 flex items-center justify-center text-red-600"><Truck size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Regional Logistics Deadlines</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Strategically managed transport corridors dispatching scheduled shipments directly to corporate distributors and processing warehouses.</p>
              </div>
            </motion.div>

            <motion.div className="flex gap-4" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="flex-shrink-0 p-3 bg-purple-100 rounded-xl h-12 w-12 flex items-center justify-center text-purple-600"><ShieldCheck size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Strict Quality Compliance</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Every batch is graded manually. We strictly segregate outputs to guarantee your commercial channels receive flawless, unblemished Grade-A products.</p>
              </div>
            </motion.div>

            <motion.div className="flex gap-4" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <div className="flex-shrink-0 p-3 bg-orange-100 rounded-xl h-12 w-12 flex items-center justify-center text-orange-600"><CreditCard size={24} /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Transparent Trade Settlements</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Secure transaction paths matching standard enterprise workflows: RTGS/NEFT banking, corporate UPI networks, or scheduled delivery-verified payments.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* REASSURANCE STATEMENT BANNER */}
      <div className="bg-green-900 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h4 className="text-xl font-bold">Looking for Custom Processing Contracts?</h4>
            <p className="text-green-200 text-sm mt-1">We cater to specific commercial requirements including custom dehydrating setups for food manufacturing brands.</p>
          </div>
          <div className="flex gap-4 flex-shrink-0">
            <span className="px-4 py-2 bg-green-800/60 rounded-lg text-xs font-semibold uppercase tracking-wider border border-green-700">MSME (Udyam) Registered</span>
            <span className="px-4 py-2 bg-green-800/60 rounded-lg text-xs font-semibold uppercase tracking-wider border border-green-700">Sustainable No-Burn Farming</span>
          </div>
        </div>
      </div>

      {/* COMMERCIAL INQUIRY FORM */}
      <div ref={formRef} className="relative py-20 bg-gray-900 bg-cover bg-center bg-fixed" style={{ backgroundImage: `linear-gradient(rgba(17, 24, 39, 0.9), rgba(17, 24, 39, 0.85)), url('/images/Aboutusnew.webp')` }}>
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className="bg-white shadow-2xl rounded-2xl p-6 md:p-10 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Commercial Procurement Request</h2>
              <p className="text-gray-600 text-sm mt-2">Submit your current supply needs below. Our commercial desk will review constraints and respond with pricing quotes within 12 business hours.</p>
            </div>

            {submitted && (
              <motion.div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-center font-medium" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                ✅ Commercial inquiry logged successfully! A wholesale representative will connect via your selected channel.
              </motion.div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Company / Firm Name (Optional)</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="e.g. Modern Retail, Supermarket, Food Processor" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Contact Representative Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="Enter name" required />
                  {errors.name && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.name}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Commercial Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="partner@company.com" required />
                  {errors.email && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.email}</span>}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Direct Mobile Lines (WhatsApp Preferred) *</label>
                  <input type="tel" name="contact" value={formData.contact} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="10-digit primary phone number" required />
                  {errors.contact && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.contact}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Target Product Selection *</label>
                  <select name="products" value={formData.products} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition text-gray-700" required>
                    <option value="">Select a commercial profile...</option>
                    <option value="Fresh White Button Mushrooms">Fresh White Button Mushrooms (Crated Bulk)</option>
                    <option value="Fresh Oyster Mushrooms">Fresh Oyster Mushrooms (Standard Packing)</option>
                    <option value="Premium Dried Oyster Mushrooms">Premium Dried Oyster Mushrooms (Moisture Sealed)</option>
                    <option value="Custom Processing / Mixed Regular Supply">Custom Processing / Mixed Regular Contract</option>
                  </select>
                  {errors.products && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.products}</span>}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Target Volume Quantity (In KG) *</label>
                  <input type="number" name="orderQuantity" value={formData.orderQuantity} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="Minimum commercial target load" required />
                  {errors.orderQuantity && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.orderQuantity}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Destination Delivery City / Warehouse *</label>
                  <input type="text" name="deliveryAddress" value={formData.deliveryAddress} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="Full logistics location details" required />
                  {errors.deliveryAddress && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.deliveryAddress}</span>}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Required Commencement Date *</label>
                  <input type="date" name="deliveryDate" value={formData.deliveryDate} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition text-gray-600" required />
                  {errors.deliveryDate && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.deliveryDate}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Preferred Settlement Protocol *</label>
                  <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition text-gray-700" required>
                    <option value="">Select settlement choice...</option>
                    <option value="Bank Transfer">Direct Bank Transfer (RTGS / NEFT)</option>
                    <option value="Corporate UPI">Corporate UPI Address / Immediate Apps</option>
                    <option value="Delivery Settlement">Verified Cash / Spot Settle Upon Delivery</option>
                  </select>
                  {errors.paymentMethod && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.paymentMethod}</span>}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Primary Communications Channel *</label>
                  <select name="preferredContactMethod" value={formData.preferredContactMethod} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition text-gray-700" required>
                    <option value="">Select communication tool...</option>
                    <option value="Phone Call">Direct Voice Verification Call</option>
                    <option value="WhatsApp">Instant WhatsApp Corporate Chat</option>
                    <option value="Email">Official Email Communications Only</option>
                  </select>
                  {errors.preferredContactMethod && <span className="text-red-500 text-xs mt-1 block font-medium">{errors.preferredContactMethod}</span>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Special Sizing, Grading, or Logistics Notes (Optional)</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50 text-sm transition" placeholder="Mention sorting specifications, custom packing preferences, or recurring shipment patterns..." rows={3} />
              </div>

              <button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider py-4 px-6 rounded-xl shadow-md transition duration-300 flex items-center justify-center gap-2 disabled:bg-green-400" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Processing Procurement Fields...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Commercial Quote Request</span>
                  </>
                )}
              </button>
            </form>
            {errors.submit && <p className="text-red-500 text-sm mt-4 text-center font-medium">{errors.submit}</p>}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Wholesale;